import React, { useState } from 'react';
import { ArrowRight, Zap, Shield, Award, TrendingUp, ZoomIn } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import QuickQuoteForm from '../components/QuickQuoteForm';
import QuickQuoteButton from '../components/QuickQuoteButton';
import ScrollReveal from '../components/ScrollReveal';
import ImageLightbox from '../components/ImageLightbox';

const Home = () => {
  const [isQuoteFormOpen, setIsQuoteFormOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState({ isOpen: false, src: '', alt: '' });

  const openLightbox = (src, alt) => {
    setLightboxImage({ isOpen: true, src, alt });
  };

  const closeLightbox = () => {
    setLightboxImage({ isOpen: false, src: '', alt: '' });
  };
  const products = [
    {
      name: 'Copper Rods',
      description: 'High-quality copper rods for industrial applications',
      image: 'https://images.unsplash.com/photo-1584774354932-62ceb99e6053?w=800',
      features: ['High conductivity', 'Premium grade', 'Custom sizes'],
      specifications: '8mm diameter'
    },
    {
      name: 'CCR (Continuous Cast Rod)',
      description: 'Premium continuous cast copper rods for wire drawing',
      image: 'https://customer-assets.emergentagent.com/job_coppertrade/artifacts/g3yaptqi_CCR.jpg',
      features: ['Superior quality', 'Consistent dimensions', 'High purity'],
      specifications: '8mm diameter'
    },
    {
      name: 'Bare Copper Wires',
      description: 'Durable bare copper wires for electrical installations',
      image: 'https://customer-assets.emergentagent.com/job_coppertrade/artifacts/0minvzep_Bare%20Copper%20Wirepng.png',
      features: ['Excellent conductivity', 'Various gauges', 'Reliable performance'],
      specifications: 'Available in CC Grade, CCR Grade, Commercial Grade'
    },
    {
      name: 'Submersible Wires',
      description: 'Specialized wires for submersible pump applications',
      image: 'https://customer-assets.emergentagent.com/job_coppertrade/artifacts/cxsxpmu5_Submersible%20Wires.png',
      features: ['Water resistant', 'Long lasting', 'Industry certified'],
      specifications: '0.5mm to 1.5mm - Available in CC & Commercial grade as per customer requirement'
    },
    {
      name: 'Earthing Copper Wires',
      description: 'High-quality earthing wires for electrical safety systems',
      image: 'https://customer-assets.emergentagent.com/job_coppertrade/artifacts/hfk02w0z_Earthing%20Copper%20Wire.png',
      features: ['Superior conductivity', 'Corrosion resistant', 'Safety certified'],
      specifications: 'Various sizes available for grounding applications'
    },
    {
      name: 'Copper Ingots',
      description: 'High-purity copper ingots for industrial manufacturing',
      image: 'https://customer-assets.emergentagent.com/job_coppertrade/artifacts/12t4yclu_Copper%20Ingots.png',
      features: ['High purity copper', 'Standard weights', 'Quality certified'],
      specifications: '75-80 kg / 200-225 kg per ingot'
    },
    {
      name: 'Copper Strips',
      description: 'Precision copper strips for diverse industrial applications',
      image: 'https://customer-assets.emergentagent.com/job_coppertrade/artifacts/b0t74dnr_Copper%20Strips.png',
      features: ['Uniform thickness', 'Various widths', 'Excellent finish'],
      specifications: 'Custom sizes available'
    },
    {
      name: 'Copper Scrap',
      description: 'Quality copper scrap materials for recycling',
      image: 'https://images.pexels.com/photos/5279317/pexels-photo-5279317.jpeg?w=800',
      features: ['Competitive pricing', 'Bulk quantities', 'Eco-friendly'],
      specifications: 'Various types available'
    }
  ];

  const features = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Quality Assured',
      description: 'Premium grade copper products meeting industry standards'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Trusted Supplier',
      description: 'Years of experience serving industries across region'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Competitive Pricing',
      description: 'Best market rates with flexible payment options'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Planned Delivery',
      description: 'Efficient logistics aligned with your requirements'
    }
  ];

  return (
    <div className="bg-gray-50 dark:bg-slate-900 transition-colors duration-300">
      {/* Quick Quote Components */}
      <QuickQuoteButton onClick={() => setIsQuoteFormOpen(true)} />
      <QuickQuoteForm isOpen={isQuoteFormOpen} onClose={() => setIsQuoteFormOpen(false)} />
      
      {/* Image Lightbox */}
      <ImageLightbox 
        isOpen={lightboxImage.isOpen} 
        imageSrc={lightboxImage.src} 
        imageAlt={lightboxImage.alt} 
        onClose={closeLightbox} 
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background with overlay */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1584774354932-62ceb99e6053?w=1920)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/75 dark:from-slate-900/95 dark:via-slate-900/85 dark:to-slate-900/75"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-50 dark:to-slate-900"></div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-32">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="inline-block mb-6 px-6 py-3 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-2 border-cyan-500/30 rounded-full backdrop-blur-sm"
            >
              <span className="text-cyan-600 dark:text-cyan-400 text-sm font-semibold tracking-wider">STRENGTH · STRUCTURE · SUPPLY</span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-6xl md:text-7xl lg:text-8xl font-black text-gray-900 dark:text-white mb-8 leading-tight tracking-tight"
            >
              <span className="block bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 bg-clip-text text-transparent drop-shadow-lg">
                COPPERAXIS
              </span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-200 mb-6 leading-tight"
            >
              Built on Strength.<br/>
              Driven by Structure.<br/>
              Defined by Supply.
            </motion.p>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-lg text-gray-600 dark:text-gray-400 mb-10 max-w-2xl leading-relaxed"
            >
              Premium copper solutions engineered for excellence. 
              Serving industries across India with precision and reliability.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <button 
                onClick={() => setIsQuoteFormOpen(true)}
                className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold rounded-xl hover:from-cyan-700 hover:to-blue-700 transition-all duration-300 shadow-2xl shadow-cyan-500/30 hover:shadow-cyan-500/50 hover:scale-105"
              >
                Get Quick Quote
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              
              <Link 
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 dark:bg-white/5 backdrop-blur-sm text-gray-900 dark:text-white font-semibold rounded-xl border-2 border-gray-300 dark:border-white/10 hover:bg-white/20 dark:hover:bg-white/10 hover:border-cyan-400 transition-all duration-300"
              >
                Contact Us
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce"
        >
          <div className="w-6 h-10 border-2 border-amber-400/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-2 bg-amber-400 rounded-full"></div>
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white dark:bg-slate-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <ScrollReveal key={index} delay={index * 0.1}>
                <div className="group p-6 bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:to-slate-800 rounded-xl border border-gray-200 dark:border-amber-600/20 hover:border-amber-500/50 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-lg">
                  <div className="text-amber-500 dark:text-amber-400 mb-4 group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-gray-50 dark:bg-slate-900 scroll-mt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                Our <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Products</span>
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
                Engineered copper solutions for industrial excellence
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <ScrollReveal key={index} delay={index * 0.1}>
                <div className="group h-full flex flex-col bg-white dark:bg-slate-950 rounded-xl overflow-hidden border border-gray-200 dark:border-amber-600/20 hover:border-amber-500/50 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-2xl">
                  <div 
                    className="relative h-56 flex-shrink-0 overflow-hidden cursor-pointer"
                    onClick={() => openLightbox(product.image, product.name)}
                  >
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 select-none"
                      draggable="false"
                      onContextMenu={(e) => e.preventDefault()}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 dark:from-slate-950 via-gray-900/50 dark:via-slate-950/50 to-transparent"></div>
                    
                    {/* Zoom icon overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/30">
                      <div className="bg-amber-500 p-3 rounded-full">
                        <ZoomIn className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-amber-500 transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed line-clamp-2">{product.description}</p>
                    
                    {/* Specifications */}
                    <div className="mb-4 p-3 bg-amber-50 dark:bg-slate-900/50 rounded-lg border border-amber-200 dark:border-amber-600/10">
                      <p className="text-xs text-amber-700 dark:text-amber-400 font-semibold mb-1">Specifications:</p>
                      <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">{product.specifications}</p>
                    </div>
                    
                    <ul className="space-y-2 mt-auto">
                      {product.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                          <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-3 flex-shrink-0"></span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Tagline Section */}
      <section className="py-20 bg-white dark:bg-slate-900 border-y-4 border-cyan-500">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="space-y-2">
                <div className="text-4xl font-black text-cyan-600 dark:text-cyan-400">STRENGTH</div>
                <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">IN QUALITY</div>
              </div>
              <div className="space-y-2">
                <div className="text-4xl font-black text-blue-600 dark:text-blue-400">STRUCTURED</div>
                <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">OPERATIONS</div>
              </div>
              <div className="space-y-2">
                <div className="text-4xl font-black text-cyan-700 dark:text-cyan-300">DEPENDABLE</div>
                <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">SUPPLY</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served Section */}
      <section id="industries" className="py-24 bg-white dark:bg-slate-950 scroll-mt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                Industries We <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Serve</span>
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
                Powering progress across diverse industrial sectors
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Electrical Wire Manufacturers',
                description: 'Premium copper solutions for electrical wire production',
                image: 'https://customer-assets.emergentagent.com/job_coppertrade/artifacts/vvk8hnbw_Electrical%20wire%20manufacturers.png'
              },
              {
                title: 'Submersible Pump Industry',
                description: 'Specialized wires for pump manufacturers and repair services',
                image: 'https://customer-assets.emergentagent.com/job_coppertrade/artifacts/zlactsa5_Submersible%20pump%20ind.png'
              },
              {
                title: 'House Wiring Cable Industry',
                description: 'Quality bare copper wires for residential cable manufacturing',
                image: 'https://customer-assets.emergentagent.com/job_coppertrade/artifacts/nxq3safy_House%20wiring%20ind.png'
              },
              {
                title: 'Copper Melting & Recycling',
                description: 'Copper scrap for melting and recycling operations',
                image: 'https://customer-assets.emergentagent.com/job_coppertrade/artifacts/udd0b3uv_Copper%20melting.jpg'
              }
            ].map((industry, index) => (
              <ScrollReveal key={index} delay={index * 0.1}>
                <div className="group relative overflow-hidden rounded-xl bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-amber-600/20 hover:border-amber-500/50 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-lg">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={industry.image}
                      alt={industry.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 dark:from-slate-900 via-gray-900/70 dark:via-slate-900/70 to-transparent"></div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-amber-500 transition-colors">
                      {industry.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                      {industry.description}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                Ready to Work With Us?
              </h2>
              <p className="text-xl text-gray-700 dark:text-gray-300 mb-10">
                Get in touch today for premium copper products and competitive pricing
              </p>
              <button 
                onClick={() => setIsQuoteFormOpen(true)}
                className="inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-lg font-semibold rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all duration-300 shadow-xl shadow-amber-500/30 hover:shadow-amber-500/50 hover:scale-105"
              >
                Request a Quote Now
                <ArrowRight className="ml-3 w-6 h-6" />
              </button>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default Home;